import videojs from 'video.js';
import { DRM_PROVIDERS } from 'src/interfaces/enums';
import { detectEdge, detectIE, detectIOS, detectSafari } from 'src/utils/browserUtils';
import { IDRMSource } from 'src/interfaces/VideoJS';
import { base64DecodeUint8Array, base64EncodeUint8Array, arrayToString } from '../../utils/formatUtils';
import { drmPolicies } from './constants';

interface IKeySystemOptions {
  licenseURL: string;
  key: DRM_PROVIDERS;
  membership: string;
  addDrmVideoPolicy: boolean;
}
class DRMService {
  public static readonly PALLYCON_CERTIFICATE = process.env.PALLYCON_CERTIFICATE;

  selectKeySystem(): DRM_PROVIDERS {
    if (detectSafari() || detectIOS()) {
      return DRM_PROVIDERS.FAIRPLAY;
    }

    if (detectEdge() || detectIE()) {
      return DRM_PROVIDERS.PLAYREADY;
    }

    return DRM_PROVIDERS.WIDEVINE;
  }

  getKeySystems(licenseURL: string, membership: string, addDrmVideoPolicy: boolean) {
    return {
      [DRM_PROVIDERS.WIDEVINE]: this.keySystemOptions({
        licenseURL,
        key: DRM_PROVIDERS.WIDEVINE,
        membership,
        addDrmVideoPolicy,
      }),
      [DRM_PROVIDERS.PLAYREADY]: this.keySystemOptions({
        licenseURL,
        key: DRM_PROVIDERS.PLAYREADY,
        membership,
        addDrmVideoPolicy,
      }),
      [DRM_PROVIDERS.APPLE_FPS]: this.fairplayOptions(licenseURL, membership),
    };
  }

  keySystemOptions({ licenseURL, key, membership, addDrmVideoPolicy }: IKeySystemOptions) {
    const drmPolicy = addDrmVideoPolicy ? drmPolicies : {};
    return {
      url: licenseURL,
      licenseHeaders: {
        keySystem: key,
        membership,
      },
      ...drmPolicy,
    };
  }

  fairplayOptions(licenseURL: string, membership: string) {
    const options = {
      getCertificate: function (
        _emeOptions: IDRMSource,
        callback: (arg0: Error | null, arg1?: Uint8Array | undefined) => void,
      ) {
        videojs.xhr(
          {
            url: DRMService.PALLYCON_CERTIFICATE,
            method: 'GET',
            responseType: 'text',
            headers: {
              Pragma: 'Cache-Control: no-cache',
              'Cache-Control': 'max-age=0',
            },
          },
          function (err: Error | undefined, _response: videojs.XhrResponse | undefined, responseBody: string) {
            if (err) {
              callback(err);
              return;
            }
            callback(null, base64DecodeUint8Array(responseBody));
          },
        );
      },
      getContentId: function (_emeOptions: IDRMSource, initData: Uint8Array) {
        const contentId = arrayToString(initData);
        return contentId.substring(contentId.indexOf('skd://') + 6);
      },
      // eslint-disable-next-line max-params
      getLicense: function (
        _emeOptions: IDRMSource,
        _contentId: string | number | boolean,
        keyMessage: Uint8Array,
        callback: (arg0: Error | null, arg1?: Uint8Array | undefined) => void,
      ) {
        videojs.xhr(
          {
            url: licenseURL,
            method: 'POST',
            responseType: 'text',
            body: `spc=${base64EncodeUint8Array(keyMessage)}`,
            headers: {
              keySystem: 'com.apple.fairplay',
              'Content-type': 'application/x-www-form-urlencoded',
              membership,
            },
          },
          function (err: Error | undefined, _response: videojs.XhrResponse | undefined, responseBody: string) {
            if (err) {
              callback(err);
              return;
            }
            callback(null, base64DecodeUint8Array(responseBody));
          },
        );
      },
    };

    return options;
  }

  emeCallback(error: Error) {
    if (error) {
      console.error('emeCallback', error);
    }
  }
}

export default new DRMService();
